import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState
} from 'react';
import axiosConfig from 'src/Resources/axiosConfig';
import { TokenContext } from './tokenContext';
import {
  I_UserWithInheritedAccess,
  InnovEnergyUser
} from '../interfaces/UserTypes';
import { FormattedMessage } from 'react-intl';
import { I_Installation } from '../interfaces/InstallationTypes';

interface AccessContextProviderProps {
  fetchInstallationsForUser: (userId: number) => void;
  accessibleInstallationsForUser: I_Installation[];
  availableUsers: InnovEnergyUser[];
  fetchAvailableUsers: () => Promise<void>;
  usersWithDirectAccess: InnovEnergyUser[];
  fetchUsersWithDirectAccessForResource: (
    tempresourceType: string,
    id: number
  ) => void;
  usersWithInheritedAccess: I_UserWithInheritedAccess[];
  fetchUsersWithInheritedAccessForResource: (
    tempresourceType: string,
    id: number
  ) => void;
  error: boolean;
  setError: (value: boolean) => void;
  updated: boolean;
  setUpdated: (value: boolean) => void;
  updatedmessage: string;
  errormessage: string;
  setErrorMessage: (value: string) => void;
  setUpdatedMessage: (value: string) => void;
  RevokeAccessFromResource: (
    resourceType: string,
    id: number,
    resourceId: string,
    current_ResourceId: number,
    name: string
  ) => void;
}

export const AccessContext = createContext<AccessContextProviderProps>({
  fetchInstallationsForUser: () => Promise.resolve(),
  accessibleInstallationsForUser: [],
  availableUsers: [],
  fetchAvailableUsers: () => {
    return Promise.resolve();
  },
  usersWithDirectAccess: [],
  fetchUsersWithDirectAccessForResource: () => Promise.resolve(),
  usersWithInheritedAccess: [],
  fetchUsersWithInheritedAccessForResource: () => Promise.resolve(),
  error: false,
  setError: () => {},
  updated: false,
  setUpdated: () => {},
  updatedmessage: '',
  errormessage: '',
  setErrorMessage: () => {},
  setUpdatedMessage: () => {},
  RevokeAccessFromResource: () => Promise.resolve()
});

const AccessContextProvider = ({ children }: { children: ReactNode }) => {
  const [error, setError] = useState(false);
  const [errormessage, setErrorMessage] = useState('An error has occured');
  const [updated, setUpdated] = useState(false);
  const [updatedmessage, setUpdatedMessage] = useState('Successfully updated');
  const tokencontext = useContext(TokenContext);
  const { removeToken } = tokencontext;
  const [usersWithDirectAccess, setUsersWithDirectAccess] = useState<
    InnovEnergyUser[]
  >([]);
  const [availableUsers, setAvailableUsers] = useState<InnovEnergyUser[]>([]);
  const [accessibleInstallationsForUser, setAccessibleInstallationsForUser] =
    useState<I_Installation[]>([]);

  const [usersWithInheritedAccess, setUsersWithInheritedAccess] = useState<
    I_UserWithInheritedAccess[]
  >([]);

  const fetchUsersWithDirectAccessForResource = useCallback(
    async (tempresourceType: string, id: number) => {
      axiosConfig
        .get(`/GetUsersWithDirectAccess${tempresourceType}?id=${id}`)
        .then((response) => {
          if (response) {
            setUsersWithDirectAccess(response.data);
          }
        })
        .catch((error) => {
          setError(true);

          const message = (
            <FormattedMessage
              id="unableToLoadData"
              defaultMessage="Unable to load data"
            />
          ).props.defaultMessage;

          setErrorMessage(message);
        });
    },
    []
  );

  const fetchInstallationsForUser = useCallback(async (userId: number) => {
    axiosConfig
      .get(`/GetInstallationsTheUserHasAccess?userId=${userId}`)
      .then((response) => {
        if (response) {
          setAccessibleInstallationsForUser(response.data);
        }
      })
      .catch((error) => {
        setError(true);
        const message = (
          <FormattedMessage
            id="unableToLoadData"
            defaultMessage="Unable to load data"
          />
        ).props.defaultMessage;
        setErrorMessage(message);
      });
  }, []);

  const fetchUsersWithInheritedAccessForResource = useCallback(
    async (tempresourceType: string, id: number) => {
      axiosConfig
        .get(`/GetUsersWithInheritedAccess${tempresourceType}?id=${id}`)
        .then((response) => {
          if (response) {
            setUsersWithInheritedAccess(response.data);
          }
        })
        .catch((error) => {
          setError(true);
          const message = (
            <FormattedMessage
              id="unableToLoadData"
              defaultMessage="Unable to load data"
            />
          ).props.defaultMessage;
          setErrorMessage(message);
        });
    },
    []
  );

  const fetchAvailableUsers = async (): Promise<void> => {
    return axiosConfig.get('/GetAllChildUsers').then((res) => {
      setAvailableUsers(res.data);
    });
  };

  const RevokeAccessFromResource = useCallback(
    async (
      resourceType: string,
      id: number,
      resourceId: string,
      current_ResourceId: number,
      name: string
    ) => {
      axiosConfig
        .post(
          `/RevokeUserAccess${resourceType}?UserId=${id}&${resourceId}=${current_ResourceId}`
        )
        .then((response) => {
          if (response) {
            fetchUsersWithDirectAccessForResource(
              resourceType,
              current_ResourceId
            );
            fetchUsersWithInheritedAccessForResource(
              resourceType,
              current_ResourceId
            );

            const message =
              (
                <FormattedMessage
                  id="revokedAccessFromUser"
                  defaultMessage="Revoked access from user: "
                />
              ).props.defaultMessage +
              ' ' +
              name;

            setUpdatedMessage(message);

            setUpdated(true);
            setTimeout(() => {
              setUpdated(false);
            }, 3000);
          }
        })
        .catch((error) => {
          setError(true);
          const message = (
            <FormattedMessage
              id="unableToRevokeAccess"
              defaultMessage="Unable to revoke access"
            />
          ).props.defaultMessage;

          setErrorMessage(message);
        });
    },
    []
  );

  return (
    <AccessContext.Provider
      value={{
        fetchInstallationsForUser,
        accessibleInstallationsForUser,
        availableUsers,
        fetchAvailableUsers,
        usersWithDirectAccess,
        fetchUsersWithDirectAccessForResource,
        usersWithInheritedAccess,
        fetchUsersWithInheritedAccessForResource,
        error,
        setError,
        updated,
        setUpdated,
        updatedmessage,
        errormessage,
        setErrorMessage,
        setUpdatedMessage,
        RevokeAccessFromResource
      }}
    >
      {children}
    </AccessContext.Provider>
  );
};

export default AccessContextProvider;
